<template>
  <div class="dropdown-container" ref="dropdownContainer">
    <button
      class="language-button"
      @click="toggleDropdown"
      :aria-expanded="dropdownOpen.toString()"
      aria-haspopup="listbox"
    >
      <img
        v-if="currentLanguage.flag"
        :src="currentLanguage.flag"
        class="language-flag"
        :alt="currentLanguage.label"
      />
      <span class="language-label">{{ currentLanguage.label }}</span>
      <span class="arrow" :class="{ open: dropdownOpen }">▼</span>
    </button>

    <transition name="dropdown-fade">
      <div v-if="dropdownOpen" class="dropdown-menu" role="listbox">
        <div
          v-for="lang in dropdownLanguages"
          :key="lang.code"
          class="dropdown-item"
          :class="{ active: lang.code === currentLanguage.code }"
          role="option"
          :aria-selected="lang.code === currentLanguage.code"
          @click="setLanguage(lang)"
        >
          <img
            v-if="lang.flag"
            :src="lang.flag"
            class="dropdown-flag"
            :alt="lang.label"
          />
          <span class="dropdown-label">{{ lang.label }}</span>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { mapState } from "vuex";

export default {
  name: "CustomLanguageDropdown",

  data() {
    return {
      dropdownOpen: false,

      // Mockdata flags - corrected accurate flag designs
      fallbackFlags: {
        et: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 990 630'%3E%3Cpath fill='%230072ce' d='M0 0h990v210H0z'/%3E%3Cpath fill='%23000' d='M0 210h990v210H0z'/%3E%3Cpath fill='%23fff' d='M0 420h990v210H0z'/%3E%3C/svg%3E",
        en: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 60 30'%3E%3Cdefs%3E%3CclipPath id='a'%3E%3Cpath d='M0 0v30h60V0z'/%3E%3C/clipPath%3E%3C/defs%3E%3Cg clip-path='url(%23a)'%3E%3Cpath fill='%23012169' d='M0 0v30h60V0z'/%3E%3Cpath stroke='%23fff' stroke-width='6' d='m0 0 60 30m0-30L0 30'/%3E%3Cpath stroke='%23C8102E' stroke-width='4' d='m0 0 60 30m0-30L0 30' clip-path='url(%23a)'/%3E%3Cpath stroke='%23fff' stroke-width='10' d='M30 0v30M0 15h60'/%3E%3Cpath stroke='%23C8102E' stroke-width='6' d='M30 0v30M0 15h60' clip-path='url(%23a)'/%3E%3C/g%3E%3C/svg%3E",
        fi: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 36 22'%3E%3Crect fill='%23fff' width='36' height='22'/%3E%3Crect fill='%23003580' x='11' width='7' height='22'/%3E%3Crect fill='%23003580' y='8' width='36' height='7'/%3E%3C/svg%3E",
        lv: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1200 600'%3E%3Cpath fill='%239e3039' d='M0 0h1200v600H0z'/%3E%3Cpath fill='%23fff' d='M0 240h1200v120H0z'/%3E%3C/svg%3E",
        ru: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 9 6'%3E%3Cpath fill='%23fff' d='M0 0h9v2H0z'/%3E%3Cpath fill='%230039a6' d='M0 2h9v2H0z'/%3E%3Cpath fill='%23d52b1e' d='M0 4h9v2H0z'/%3E%3C/svg%3E",
      },
      fallbackLanguages: [
        { code: "et", label: "Eesti keel" },
        { code: "en", label: "English" },
        { code: "fi", label: "Suomi" },
        { code: "lv", label: "Latviešu" },
        { code: "ru", label: "Русский язык" },
      ],

      currentLanguageCode: "et",
    };
  },

  computed: {
    ...mapState({
      storeLanguages: (state) =>
        (state.wf && Array.isArray(state.wf.languages)
          ? state.wf.languages
          : []) || [],
      storeLanguageCode: (state) =>
        state.wf && state.wf.language ? state.wf.language : null,
    }),

    dropdownLanguages() {
      const fromStore =
        this.storeLanguages && this.storeLanguages.length
          ? this.storeLanguages
              .map((lang) => this.mapStoreLanguage(lang))
              .filter((lang) => !!lang)
          : [];

      const merged = new Map();

      // First add fallback languages
      this.fallbackLanguages.forEach((fallback) => {
        merged.set(fallback.code, {
          ...fallback,
          flag: this.getFallbackFlag(fallback.code),
        });
      });

      // Then override with store languages if available
      fromStore.forEach((lang) => {
        merged.set(lang.code, {
          ...merged.get(lang.code),
          ...lang,
          flag: lang.flag || this.getFallbackFlag(lang.code),
        });
      });

      const result = Array.from(merged.values());
      console.log("Dropdown languages with flags:", result);
      return result;
    },

    currentLanguage() {
      const code = (this.currentLanguageCode || "").toLowerCase();
      return (
        this.dropdownLanguages.find((lang) => lang.code === code) ||
        this.dropdownLanguages[0] || {
          code: "",
          label: "",
          flag: null,
        }
      );
    },
  },

  mounted() {
    const saved = localStorage.getItem("selectedLanguage");
    if (saved) {
      this.currentLanguageCode = saved.toLowerCase();
    } else if (this.storeLanguageCode) {
      this.currentLanguageCode = this.storeLanguageCode.toLowerCase();
    }
    this.ensureLanguageSelected();

    document.addEventListener('click', this.handleClickOutside);
  },

  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside);
  },

  watch: {
    storeLanguageCode: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          const normalized = newVal.toLowerCase();
          if (this.currentLanguageCode !== normalized) {
            this.currentLanguageCode = normalized;
            localStorage.setItem("selectedLanguage", normalized);
          }
        }
      },
    },
    dropdownLanguages(newList, oldList) {
      if (!oldList.length && newList.length) {
        this.ensureLanguageSelected();
      }
    },
  },

  methods: {
    handleClickOutside(event) {
      if (this.dropdownOpen && this.$refs.dropdownContainer && !this.$refs.dropdownContainer.contains(event.target)) {
        this.dropdownOpen = false;
      }
    },

    ensureLanguageSelected() {
      if (!this.dropdownLanguages.length) {
        return;
      }
      const target =
        this.dropdownLanguages.find(
          (lang) => lang.code === this.currentLanguageCode
        ) || this.dropdownLanguages[0];

      if (target) {
        this.setLanguage(target, { silent: true });
      }
    },

    mapStoreLanguage(lang) {
      const rawCode =
        (typeof lang.getName === "function" && lang.getName()) ||
        lang.name ||
        lang.code;

      if (!rawCode) {
        return null;
      }

      const code = rawCode.toLowerCase();
      const label =
        (typeof lang.getNativeName === "function" && lang.getNativeName()) ||
        lang.label ||
        lang.name ||
        rawCode.toUpperCase();

      // Don't try to get Wayfinder flag if flagImage doesn't exist
      // This prevents broken URLs from being used
      const wayfinderFlagId = (typeof lang.getFlagImage === "function" && lang.getFlagImage()) || lang.flagImage;
      
      return {
        code,
        label,
        flag: this.getFallbackFlag(code), // Always use fallback for now since Wayfinder flags aren't configured
        original: lang,
      };
    },

    getFallbackFlag(code) {
      return this.fallbackFlags[code] || null;
    },

    getFlagUrl(flagId) {
      if (!flagId) {
        return null;
      }

      try {
        const api =
          (typeof window !== "undefined" && window.WayfinderAPI) || undefined;

        if (api && typeof api.getURL === "function") {
          return api.getURL("images", "thumbnail", [flagId]);
        }
      } catch (error) {
        console.warn("Failed to resolve Wayfinder flag image", error);
      }

      return null;
    },

    toggleDropdown() {
      this.dropdownOpen = !this.dropdownOpen;
    },

    setLanguage(lang, options = {}) {
      if (!lang) {
        return;
      }

      const codeLower = (lang.code || "").toLowerCase();
      this.currentLanguageCode = codeLower;
      this.dropdownOpen = false;
      localStorage.setItem("selectedLanguage", codeLower);

      if (options.silent) {
        return;
      }

      this.callWayfinderSetLanguage(codeLower);
    },

    callWayfinderSetLanguage(codeLower) {
      const protoWf = this.$wf || this.$wayfinder || null;
      const winWf = window.wayfinder || window.Wayfinder || null;
      const wfInstance = protoWf || winWf;

      if (wfInstance && typeof wfInstance.setLanguage === "function") {
        try {
          wfInstance.setLanguage(codeLower);
          console.log("Called wf.setLanguage(", codeLower, ")");
        } catch (e) {
          console.warn("wf.setLanguage failed:", e);
        }
      } else {
        console.warn("Wayfinder instance not found");
      }
    },
  },
};
</script>

<style scoped>
.dropdown-container {
  position: relative;
  display: inline-block;
}

.language-button {
  display: flex;
  align-items: center;
  margin-right: 1.5rem;
  gap: 10px;
  color: #333;
  padding: 10px 16px;
  border-radius: 8px;
  font-size: 1.6rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: 1rem;
  border: 8px;
}

.language-button.active{
  background-color: #f0f0f0
}

.language-button:hover {
  background: #f5f5f5;
  border-color: #f3f3f3ee;
  
}

.language-flag {
  width: 28px;
  height: 21px;
  border-radius: 4px;
  object-fit: cover;
  flex-shrink: 0;
  display: block;
}

.language-label {
  font-weight: 600;
  letter-spacing: 0.3px;
  font-size: 1.5rem;
}

.arrow {
  font-size: 12px;
  transition: transform 0.2s ease;
  margin-left: 4px;
}

.arrow.open {
  transform: rotate(180deg);
}

.dropdown-menu {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  left: 0;  /* Add this - makes dropdown same width as button */
  width: 100%;  /* Add this - forces dropdown to match button width */
  max-height: 400px;
  overflow-y: auto;
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  z-index: 9999;
  border: 2px solid #e0e0e0;
}

.dropdown-fade-enter-active,
.dropdown-fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.dropdown-fade-enter,
.dropdown-fade-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

.dropdown-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  cursor: pointer;
  transition: background 0.2s ease;
  color: #333;
  font-size: 1.6rem;
  font-weight: 500;
  border-bottom: 1px solid #f0f0f0;
  white-space: nowrap;
  overflow: hidden;  /* Add this - hides overflow text */
  text-overflow: ellipsis;
}

.dropdown-item:last-child {
  border-bottom: none;
}

.dropdown-item:hover {
  background: #f5f5f5;
}

.dropdown-item.active {
  background: #e8f4fd;
  font-weight: 700;
  color: #1976d2;
}

.dropdown-flag {
  width: 28px;
  height: 21px;
  border-radius: 4px;
  object-fit: cover;
  flex-shrink: 0;
  display: block;
}

.dropdown-label {
  flex: 1;
}

/* Mobile specific adjustments */
@media (max-width: 480px) {
  .language-button {
    padding: 5px 8px;
    font-size: 1.3rem;
    gap: 5px;
  }
  
  .language-flag,
  .dropdown-flag {
    width: 18px;
    height: 14px;
  }
  
  .language-label {
    font-size: 1.2rem;
  }
  
  .dropdown-menu {
    min-width: 140px;
    max-width: 180px;
  }
  
  .dropdown-item {
    padding: 8px 12px;
    font-size: 1.3rem;
  }
}
</style>